"use client"

import { cn } from "@/utils/utils"
import { useRef, useState, type ReactNode } from "react"
import { FiChevronDown } from "react-icons/fi"

/**
 * A two-column card grid that reveals its tail behind a "Visa fler" / "Visa färre" toggle.
 *
 * Cards arrive as rendered nodes rather than data, so a server component can keep doing its
 * Storyblok fetch and render the cards itself — only this wrapper ships to the browser.
 *
 * Callers decide where the cut falls and only mount this when something is actually hidden;
 * below the threshold a plain grid is cheaper and needs no JavaScript.
 */
export const ExpandableCardGrid = ({
  initial,
  rest,
  className
}: {
  initial: ReactNode
  rest: ReactNode
  /** Applied to the always-visible grid, for callers that need their own top spacing. */
  className?: string
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  // Clip during the open/close animation; allow overflow once fully expanded so card shadows
  // aren't cut. Flipped to true only when the expand transition finishes.
  const [showOverflow, setShowOverflow] = useState(false)
  const toggleRef = useRef<HTMLDivElement>(null)

  const handleToggle = () => {
    if (!isExpanded) {
      setIsExpanded(true)
      return
    }

    setShowOverflow(false) // clip again immediately so shadows don't linger while collapsing
    setIsExpanded(false)

    // Keep the toggle button visually pinned during the collapse. Without this the shrinking
    // content clamps the scroll to the new (shorter) bottom and dumps the user at the footer.
    // Re-anchoring each frame instead eases the initial cards back into view above it.
    const anchor = toggleRef.current
    if (!anchor) return
    const startTop = anchor.getBoundingClientRect().top
    let rafId = requestAnimationFrame(function keepAnchored() {
      window.scrollBy(0, anchor.getBoundingClientRect().top - startTop)
      rafId = requestAnimationFrame(keepAnchored)
    })
    // Stop once the collapse (delay-300 + duration-500) has settled.
    window.setTimeout(() => cancelAnimationFrame(rafId), 900)
  }

  return (
    <>
      <div className={cn("grid grid-cols-1 gap-6 lg:grid-cols-2", className)}>{initial}</div>

      {/* grid-rows 0fr↔1fr animates to the true content height. Stays mounted so it can animate
          both in and out; `inert` while collapsed pulls the hidden cards out of tab order and the
          a11y tree, so keyboard users land on the toggle rather than off-screen cards. */}
      <div
        inert={!isExpanded}
        className={cn(
          "grid transition-[grid-template-rows] delay-300 ease-out",
          isExpanded
            ? "grid-rows-[1fr] duration-700" // after the chevron (300ms), reveal
            : "grid-rows-[0fr] duration-500" // after the chevron, collapse
        )}
        onTransitionEnd={e => {
          if (e.propertyName !== "grid-template-rows") return // ignore bubbled child transitions
          if (isExpanded) setShowOverflow(true) // reveal overflow only once the EXPAND has settled
        }}
      >
        <div className={cn(showOverflow ? "overflow-visible" : "overflow-hidden")}>
          {/* pb-8 keeps the gap below the last row inside the animated height, so it grows with the
              curtain instead of pushing the next section down immediately. */}
          <div className="mt-8 grid grid-cols-1 gap-6 pb-8 lg:grid-cols-2">{rest}</div>
        </div>
      </div>

      <div ref={toggleRef} className="mt-2 flex justify-center hover:opacity-75 lg:mt-10">
        <button
          type="button"
          onClick={handleToggle}
          className="flex items-center gap-[4px] py-2 text-[15px] font-semibold leading-normal tracking-normal text-neutral-90 lg:text-xl lg:font-bold"
        >
          {isExpanded ? "Visa färre" : "Visa fler"}
          <FiChevronDown
            className={cn(
              "h-[25px] w-[25px] text-[#6B6F67] transition-transform duration-300 lg:h-8 lg:w-8",
              isExpanded && "rotate-180"
            )}
          />
        </button>
      </div>
    </>
  )
}

export default ExpandableCardGrid
